import { DEFAULT_MESSAGE_RECEIPTS_THROTTLE_MS, FEATURES, USE_DUAL_STACK_DEFAULT_VALUE } from "./constants";
import { LogManager } from "./log";

class GlobalConfigImpl {
    constructor() {
        this.stage = "prod";
        this.region = "us-west-2";
        this.regionOverride = ""; //used for region failover
        this.cell = "1";
        this.reconnect = true;
        let self = this;
        this.logger = LogManager.getLogger({
            prefix: "ChatJS-GlobalConfig",
        });
        this.features = new Proxy([], {
            set: (target, property, value) => {
                this.stage !== "test-stage2" && this.logger.info("new features added, initialValue: "
                + target[property] + " , newValue: " + value, Array.isArray(target[property]));
                let oldVal = target[property];
                //fire change listeners
                if (Array.isArray(value)) {
                    value.forEach(feature => {
                        //if a new feature is added
                        if (Array.isArray(oldVal) && oldVal.indexOf(feature) === -1 &&
                                Array.isArray(self.featureChangeListeners[feature])) {

                            self.featureChangeListeners[feature].forEach(callback => callback());
                            self._cleanFeatureChangeListener(feature);
                        }
                    });
                }
                //change the value in this.features object.
                target[property] = value;
                return true;
            }
        });
        this.setFeatureFlag(FEATURES.MESSAGE_RECEIPTS_ENABLED); // message receipts enabled by default
        this.messageReceiptThrottleTime = DEFAULT_MESSAGE_RECEIPTS_THROTTLE_MS;
        this.featureChangeListeners = [];
        this.customUserAgentSuffix = "";
        this.customChatClient = null;
        this._messageReceiptsExplicitlyConfigured = false;
        this.useDualStack = USE_DUAL_STACK_DEFAULT_VALUE;
    }
    update(configInput) {
        // A truthy primitive would reach the `in` test below and throw, aborting the rest
        // of global initialization.
        var config = (configInput && typeof configInput === "object") ? configInput : {};
        this.stage = config.stage || this.stage;
        this.region = config.region || this.region;
        this.cell = config.cell || this.cell;
        this.endpointOverride = config.endpoint || this.endpointOverride;
        this.reconnect = config.reconnect === false ? false : this.reconnect;
        this.messageReceiptThrottleTime = config.throttleTime || this.messageReceiptThrottleTime || DEFAULT_MESSAGE_RECEIPTS_THROTTLE_MS;
        if (Array.isArray(config.features)) {
            this.features["values"] = [...config.features];
        } else if (!Array.isArray(this.features["values"])) {
            this.features["values"] = new Array();
        }
        this.customUserAgentSuffix = config.customUserAgentSuffix || this.customUserAgentSuffix;
        // Only a real boolean flips the flag. JS callers can pass anything (e.g. the string
        // "false" read from env or a config file, which is truthy), so a non-boolean is ignored
        // with a warning instead of silently picking an endpoint the caller did not ask for.
        if (typeof config.useDualStack === "boolean") {
            this.useDualStack = config.useDualStack;
        } else if (config.useDualStack !== undefined) {
            this.logger.warn("useDualStack must be a boolean; ignoring the provided value and keeping " +
                "the current setting.", { providedType: typeof config.useDualStack, useDualStack: this.useDualStack });
        }

        if ("customChatClient" in config) {
            this.customChatClient = config.customChatClient;
        }
    }

    updateStageRegionCell(config) {
        if (config) {
            this.stage = config.stage || this.stage;
            this.region = config.region || this.region;
            this.cell = config.cell || this.cell;
        }
    }

    getCell() {
        return this.cell;
    }

    updateThrottleTime(throttleTime) {
        this.messageReceiptThrottleTime = throttleTime || this.messageReceiptThrottleTime || DEFAULT_MESSAGE_RECEIPTS_THROTTLE_MS;
    }

    updateRegionOverride(regionOverride) {
        this.regionOverride = regionOverride;
    }

    getMessageReceiptsThrottleTime() {
        return this.messageReceiptThrottleTime;
    }

    getStage() {
        return this.stage;
    }

    getRegion() {
        return this.region;
    }

    getRegionOverride() {
        return this.regionOverride;
    }

    getCustomUserAgentSuffix() {
        return this.customUserAgentSuffix;
    }

    getEndpointOverride() {
        return this.endpointOverride;
    }

    /** The client registered via setGlobalConfig({ customChatClient }), or null. */
    getCustomChatClient() {
        return this.customChatClient;
    }

    removeFeatureFlag(feature) {
        if (!this.isFeatureEnabled(feature)) {
            return;
        }
        const index = this.features["values"].indexOf(feature);
        if (index > -1) {
            this.features["values"].splice(index, 1);
        }
    }

    setMessageReceiptsExplicitlyConfigured() {
        this._messageReceiptsExplicitlyConfigured = true;
    }

    isMessageReceiptsExplicitlyConfigured() {
        return this._messageReceiptsExplicitlyConfigured;
    }

    setFeatureFlag(feature) {
        if(this.isFeatureEnabled(feature)) {
            return;
        }
        const featureValues = Array.isArray(this.features["values"]) ? this.features["values"] : [];
        this.features["values"] = [...featureValues, feature];
    }

    getDualStackFlag() {
        return this.useDualStack;
    }

    //private method
    _registerFeatureChangeListener(feature, callback) {
        if (!this.featureChangeListeners[feature]) {
            this.featureChangeListeners[feature] = [];
        }
        this.featureChangeListeners[feature].push(callback);
    }

    //private method
    _cleanFeatureChangeListener(feature) {
        delete this.featureChangeListeners[feature];
    }

    isFeatureEnabled(feature, callback) {
        if(Array.isArray(this.features["values"]) &&
            this.features["values"].indexOf(feature) !== -1) {
            if (typeof callback === "function") {
                return callback();
            }
            return true;
        }
        if (typeof callback === "function") {
            this._registerFeatureChangeListener(feature, callback);
        }
        return false;
    }
}

const GlobalConfig = new GlobalConfigImpl();

export { GlobalConfig };
